/**
 * MoveCharacterDialog - Dialog for moving a character to a new location
 */

import type { Component, ChangeEvent } from 'react';
import { Character } from '../models/Character';
import Dialog from './Dialog';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

// Forward declarations
declare const TheGame: any;
declare const Finder: any;
declare const GameCommands: any;
declare const VoidRoomGuid: string;

interface MoveCharacterDialogProps {
    character: Character;
    onSave: (roomId: string) => void;
    onCancel: () => void;
}

interface MoveCharacterDialogState {
    locationType: string;
    selectedRoom: string;
}

class MoveCharacterDialog extends ReactComponent<MoveCharacterDialogProps, MoveCharacterDialogState> {
    constructor(props: MoveCharacterDialogProps) {
        super(props);
        const currentRoom = props.character.CurrentRoom || '';
        this.state = {
            locationType: currentRoom === (VoidRoomGuid || '<Void>') ? 'void' : currentRoom ? 'room' : 'void',
            selectedRoom: currentRoom
        };
    }

    handleSave = (): void => {
        let roomId = '';
        if (this.state.locationType === 'void') {
            roomId = VoidRoomGuid || '<Void>';
        } else if (this.state.locationType === 'current') {
            roomId = TheGame?.Player?.CurrentRoom || '';
        } else {
            roomId = this.state.selectedRoom;
        }
        this.props.onSave(roomId);
    };

    render(): any {
        const DialogComponent = (window as any).Dialog;
        if (!DialogComponent) return null;

        // Get available rooms, sorted alphabetically
        const rooms = (TheGame?.Rooms || []).sort((a: any, b: any) => 
            a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' })
        );

        return React.createElement(DialogComponent, {
            title: `Move Character: ${this.props.character.Charname}`,
            onClose: this.props.onCancel,
            actionButtons: React.createElement(React.Fragment, null,
                React.createElement('button', {
                    className: 'btn',
                    onClick: this.props.onCancel
                }, 'Cancel'),
                React.createElement('button', {
                    className: 'btn',
                    onClick: this.handleSave
                }, 'Move')
            ),
            closeOnBackdropClick: false
        },
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '16px' } },
                React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                    React.createElement('label', { style: { fontWeight: 'bold' } }, 'Location Type:'),
                    React.createElement('select', {
                        value: this.state.locationType,
                        onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                            this.setState({ 
                                locationType: e.target.value,
                                selectedRoom: e.target.value === 'current' ? (TheGame?.Player?.CurrentRoom || '') : this.state.selectedRoom
                            });
                        },
                        style: {
                            padding: '8px',
                            fontFamily: 'var(--font-game-text)',
                            fontSize: '14px',
                            border: '1px solid rgba(0, 0, 0, 0.2)',
                            borderRadius: '4px',
                            background: 'var(--main-bg-color)',
                            color: 'var(--main-text-color)'
                        }
                    },
                        React.createElement('option', { value: 'void' }, 'No place (Void)'),
                        React.createElement('option', { value: 'current' }, 'Current room'),
                        React.createElement('option', { value: 'room' }, 'Select room...')
                    )
                ),
                this.state.locationType === 'room' && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                    React.createElement('label', { style: { fontWeight: 'bold' } }, 'Room:'),
                    React.createElement('select', {
                        value: this.state.selectedRoom,
                        onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                            this.setState({ selectedRoom: e.target.value });
                        },
                        style: {
                            padding: '8px',
                            fontFamily: 'var(--font-game-text)',
                            fontSize: '14px',
                            border: '1px solid rgba(0, 0, 0, 0.2)',
                            borderRadius: '4px',
                            background: 'var(--main-bg-color)',
                            color: 'var(--main-text-color)'
                        }
                    },
                        React.createElement('option', { value: '' }, 'Select room...'),
                        rooms.map((room: any) =>
                            React.createElement('option', { key: room.UniqueID, value: room.UniqueID }, room.Name)
                        )
                    )
                )
            )
        );
    }
}

(window as any).MoveCharacterDialog = MoveCharacterDialog;
export default MoveCharacterDialog;
